/**
 * 紹介キャンペーン（紹介する側）の訴求を出してよいかの判定。
 *
 * 確認・完了画面のシェア枠（ShareInviteBlock）と、ヤギプライム導線まわりの
 * キャンペーンバナー（ReferralCampaignBanner）が同じ条件で出し分けるための共有。
 * 条件をコピーすると片方だけ直して食い違うので、ここを正本にする。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useReferralStatus } from '@/composables/yagiprime/useReferralStatus'
import campaign from '@/utils/yagiprime/referralCampaign.json'

/**
 * authReady の解決を待つ上限。ここを無条件に待つと、Firebase の初期化を
 * 取りこぼした環境でキャンペーン枠が永久に出ない（＝キャンペーンが黙って死ぬ）。
 */
const AUTH_WAIT_MS = 5000

export function useReferralOfferVisibility() {
  // 未ログインは加入状態を判定できないが、既存会員ではありえないので訴求を出す
  const { isAuthenticated, whenAuthReady } = useAuth()
  // 出し分けはサーバーの can_refer 一本。被紹介者・キャンペーン前から継続
  // 加入している既存 Prime 会員はサーバー側で false になる。
  // ⚠️ ここで「未加入か」を条件に足してはいけない。期間中に加入した紹介者も
  // 対象のままで、成立するたびにサーバーが trial_end を伸ばす。
  // 判定が返るまでは出さない（誤訴求を避ける）
  const { loadStatus, canRefer, isStatusChecked } = useReferralStatus()
  // 認証が確定したか。useAuth の user は module 級 ref の初期値 null で、
  // Firebase のセッション復元が終わるまで isAuthenticated は false を返す。
  // これを待たずに描画すると、ログイン済みの被紹介者・既存 Prime 会員にも
  // 初回描画で「未ログイン」扱いのキャンペーン枠（＝成立しない紹介URL）が
  // 一瞬見える。確定してから出し分ける
  const authSettled = ref(false)
  const showCampaignOffer = computed(
    () =>
      campaign.isActive &&
      authSettled.value &&
      (!isAuthenticated.value || (isStatusChecked.value && canRefer.value))
  )

  onMounted(() => {
    if (!campaign.isActive) return
    loadStatus()
    // 無条件に待つと、Firebase の初期化を取りこぼした環境でキャンペーン枠が
    // 永久に出ない。待ちきれない場合はその時点の認証状態で出し分ける
    Promise.race([
      whenAuthReady(),
      new Promise((resolve) => setTimeout(resolve, AUTH_WAIT_MS))
    ]).then(() => {
      authSettled.value = true
    })
  })

  // 未ログインで枠を見せている最中にログインされたら判定を取り直す。
  // 取り直さないと isStatusChecked が false のままで、ログインした直後に
  // キャンペーン枠が消えてしまう
  watch(isAuthenticated, (authenticated) => {
    if (!authenticated || !campaign.isActive) return
    loadStatus()
  })

  return { isAuthenticated, showCampaignOffer }
}
